import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Loader2, Download, KeyRound, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputCls, btnPrimary, useApi } from "@/components/portal/ui";

function Section({ title, children, testid }) {
  return (
    <div className="space-y-4 pt-2 first:pt-0" data-testid={testid}>
      <p className="text-sm font-extrabold text-slate-900 border-b border-slate-100 pb-2">{title}</p>
      {children}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="bg-slate-50 rounded-2xl p-3"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p><p className="font-bold text-slate-900 mt-0.5 truncate">{value}</p></div>
);

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Konfirmasi password tidak sama");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password berhasil diubah");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Section title="Keamanan" testid="profile-security-section">
      <div className="space-y-3">
        <Field label="Password Saat Ini"><input data-testid="cp-current-input" type="password" className={inputCls} value={form.current_password} onChange={set("current_password")} required /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Password Baru"><input data-testid="cp-new-input" type="password" minLength={6} className={inputCls} value={form.new_password} onChange={set("new_password")} required /></Field>
          <Field label="Konfirmasi"><input data-testid="cp-confirm-input" type="password" minLength={6} className={inputCls} value={form.confirm} onChange={set("confirm")} required /></Field>
        </div>
        <button type="button" onClick={submit} disabled={loading} data-testid="change-password-button" className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white font-bold py-3 px-6 rounded-xl hover:bg-slate-800 active:scale-95 transition disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4" /> Ubah Password</>}
        </button>
      </div>
    </Section>
  );
}

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const KEYS = ["name", "phone", "birth_date", "birth_place", "address", "city", "occupation", "education", "blood_type",
    "baptism_date", "baptism_place", "marital_status", "spouse_name", "children", "emergency_name", "emergency_phone"];
  const [form, setForm] = useState(Object.fromEntries(KEYS.map((k) => [k, user[k] || ""])));
  const { data: home } = useApi("/portal/home");
  const { data: meta, reload: reloadMeta } = useApi("/portal/profile");
  const locked = meta && !meta.editable;
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Ukuran foto maksimal 5MB");
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.put("/portal/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      toast.success("Foto profil diperbarui");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/portal/profile", form);
      setUser(data);
      reloadMeta();
      toast.success("Biodata disimpan & otomatis tersinkron ke data jemaat OPC");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  const out = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div data-testid="portal-profile">
      <div className="relative rounded-b-[2.5rem] bg-blue-600 text-white px-6 pt-12 pb-16 text-center overflow-hidden">
        <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/10" />
        <div className="relative w-24 h-24 mx-auto">
          {user.picture ? <img src={fileUrl(user.picture)} alt="" className="w-24 h-24 rounded-full object-cover ring-4 ring-white/40 shadow-xl" /> : <span className="w-24 h-24 rounded-full bg-white/20 ring-4 ring-white/40 grid place-items-center text-4xl font-black">{user.name[0]}</span>}
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingPhoto} data-testid="profile-photo-button" aria-label="Ubah foto profil" className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-white text-blue-600 grid place-items-center shadow-lg ring-2 ring-blue-600 active:scale-90 transition disabled:opacity-60">
            {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPhoto} className="hidden" data-testid="profile-photo-input" />
        </div>
        <h1 className="relative text-2xl font-extrabold mt-4" data-testid="profile-name">{user.name}</h1>
        <p className="relative text-sm text-blue-100 mt-1" data-testid="profile-nig">NIG {user.nig} · {user.email}</p>
      </div>
      <form onSubmit={save} className="px-5 -mt-8 relative z-10 space-y-4" data-testid="profile-form">
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-4">
          <div className={`rounded-2xl px-4 py-3 text-xs font-semibold ${locked ? "bg-amber-50 text-amber-800" : "bg-blue-50 text-blue-700"}`} data-testid="profile-lock-notice">
            {locked
              ? `Anda telah mencapai batas ${meta?.max_edits || 3}x perubahan biodata bulan ini. Silakan coba lagi bulan depan atau hubungi admin/OPC bila ada data yang harus segera diperbaiki.`
              : `Biodata dapat diubah maksimal ${meta?.max_edits || 3}x per bulan. Sisa ${meta?.edits_left ?? meta?.max_edits ?? 3}x bulan ini. Setelah disimpan, data langsung tersinkron ke data jemaat di portal OPC.`}
          </div>
          <Section title="Status Jemaat & Community" testid="profile-status-section">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Status" value={user.member_status || "Jemaat"} />
              <Info label="Lencana" value={home?.level?.name || "—"} />
              <Info label="Relationship Community" value={home?.komsel?.name || "Belum bergabung"} />
              <Info label="Pelayanan" value={user.ministry || "Belum ada"} />
            </div>
          </Section>
          <Section title="Biodata">
            <Field label="Nama Lengkap"><input data-testid="profile-name-input" className={inputCls} value={form.name} onChange={set("name")} required /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tempat Lahir"><input data-testid="profile-birthplace-input" className={inputCls} value={form.birth_place} onChange={set("birth_place")} /></Field>
              <Field label="Tanggal Lahir"><input data-testid="profile-birth-input" type="date" className={inputCls} value={form.birth_date} onChange={set("birth_date")} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Pekerjaan"><input data-testid="profile-occupation-input" className={inputCls} value={form.occupation} onChange={set("occupation")} /></Field>
              <Field label="Pendidikan Terakhir"><select data-testid="profile-education-select" className={inputCls} value={form.education} onChange={set("education")}>{["", "SD", "SMP", "SMA/SMK", "D3", "S1", "S2", "S3"].map((o) => <option key={o} value={o}>{o || "Pilih…"}</option>)}</select></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Golongan Darah"><select data-testid="profile-blood-select" className={inputCls} value={form.blood_type} onChange={set("blood_type")}>{["", "A", "B", "AB", "O"].map((o) => <option key={o} value={o}>{o || "Pilih…"}</option>)}</select></Field>
              <Field label="Tgl. Baptis"><input data-testid="profile-baptism-input" type="date" className={inputCls} value={form.baptism_date} onChange={set("baptism_date")} /></Field>
            </div>
            <Field label="Tempat Baptis"><input data-testid="profile-baptism-place-input" className={inputCls} value={form.baptism_place} onChange={set("baptism_place")} /></Field>
          </Section>
          <Section title="Keluarga">
            <Field label="Status Pernikahan"><select data-testid="profile-marital-select" className={inputCls} value={form.marital_status} onChange={set("marital_status")}>{["", "Belum Menikah", "Menikah", "Duda/Janda"].map((o) => <option key={o} value={o}>{o || "Pilih…"}</option>)}</select></Field>
            <Field label="Nama Pasangan"><input data-testid="profile-spouse-input" className={inputCls} value={form.spouse_name} onChange={set("spouse_name")} /></Field>
            <Field label="Nama Anak (pisahkan koma)"><input data-testid="profile-children-input" className={inputCls} value={form.children} onChange={set("children")} /></Field>
          </Section>
          <Section title="Kontak">
            <Field label="No. WhatsApp"><input data-testid="profile-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+62 8xx xxxx xxxx" /></Field>
            <Field label="Alamat"><input data-testid="profile-address-input" className={inputCls} value={form.address} onChange={set("address")} placeholder="Alamat domisili" /></Field>
            <Field label="Kota / Kecamatan"><input data-testid="profile-city-input" className={inputCls} value={form.city} onChange={set("city")} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kontak Darurat"><input data-testid="profile-emergency-name-input" className={inputCls} value={form.emergency_name} onChange={set("emergency_name")} /></Field>
              <Field label="No. Darurat"><input data-testid="profile-emergency-phone-input" className={inputCls} value={form.emergency_phone} onChange={set("emergency_phone")} /></Field>
            </div>
          </Section>
          <ChangePassword />
          <button data-testid="profile-save-button" disabled={loading || locked} className={`${btnPrimary} w-full disabled:opacity-50`}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : locked ? "Batas bulan ini tercapai" : "Simpan Perubahan"}</button>
        </div>
        <Link to="/portal/ministry" data-testid="profile-ministry-link" className="flex items-center gap-4 bg-white rounded-3xl p-5 border border-slate-100 shadow-sm hover:-translate-y-1 transition duration-300"><span className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center"><ShieldCheck className="w-5 h-5" /></span><span className="font-bold text-slate-900">My Ministry</span></Link>
        {user.role && user.role !== "member" && (
          <Link to="/admin" data-testid="profile-admin-link" className="flex items-center gap-4 bg-slate-900 text-white rounded-3xl p-5 hover:-translate-y-1 transition duration-300"><span className="w-11 h-11 rounded-2xl bg-white/10 grid place-items-center"><ShieldCheck className="w-5 h-5" /></span><span className="font-bold">Portal Admin (OPC)</span></Link>
        )}
        <p className="flex items-center gap-2 text-xs text-slate-500 px-1"><Download className="w-4 h-4" /> Install aplikasi: buka menu browser → "Tambahkan ke Layar Utama".</p>
        <button type="button" onClick={out} data-testid="profile-logout-button" className="w-full inline-flex items-center justify-center gap-2 bg-red-50 text-red-600 font-bold py-3 md:py-4 px-6 rounded-xl hover:bg-red-100 active:scale-95 transition"><LogOut className="w-4 h-4" /> Keluar Akun</button>
      </form>
    </div>
  );
}
